import { ButtonIcon, Callout, LinkUnbrokenIcon, Tooltip } from 'canoe';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import EventListener from 'react-event-listener';
import { Editor, Element, Path, Range } from 'slate';
import { ReactEditor, useSlate } from 'slate-react';

import { hotkeys } from 'commons/hotkeys';
import { TooltipBodyWithShortcuts } from 'craft/components/TooltipBodyWithShortcuts';
import { BuiltInComponents, NO_ACTION } from 'craft/constants';
import { unwrapLink } from 'craft/rte/plugins/withLink/unwrapLink';
import { LinkDefinition, wrapLink } from 'craft/rte/plugins/withLink/wrapLink';
import { useViewportDom } from 'craft/state/viewport/selectors';
import { SlateNode } from 'craft/types';

import { LinkToolbar } from './LinkToolbar';

const getDefinitionForLinkAtRange = (rteEditor: Editor, range: Range): LinkDefinition | null => {
  const rangePath = Editor.path(rteEditor, range);

  // the range should be a child (or equals) of any Link found within the rteEditor
  const [link] = Array.from(
    Editor.nodes<SlateNode>(rteEditor, {
      at: range,
      match: (n, path) => {
        return (
          (Path.isDescendant(rangePath, path) || Path.equals(rangePath, path)) &&
          Element.isElement(n) &&
          (n as SlateNode).type === 'Link'
        );
      },
    }),
  );

  if (!link) {
    return null;
  }

  const [node] = link;

  if (!Element.isElement(node)) {
    return null;
  }

  const variables = node.data.custom?.variables;

  return {
    href: node.data.props.href,
    variables: variables ? Object.values(variables) : undefined,
  };
};

interface LinkButtonProps {
  disabled?: boolean;
}

export const LinkButton: React.FC<LinkButtonProps> = ({ disabled }: LinkButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [isToolbarVisible, setToolbarVisible] = useState(false);
  const rteEditor = useSlate() as ReactEditor;
  const { viewportWrapperEl } = useViewportDom();

  const range = rteEditor.selection;
  const hasRange = !!range;

  const showToolbar = useCallback(() => {
    setToolbarVisible(true);
  }, []);

  const closeToolbar = useCallback(() => {
    setToolbarVisible(false);
    if (!range) {
      return;
    }

    const windowSelection = window.getSelection();
    windowSelection?.removeAllRanges();

    if (!range.anchor && !range.focus) {
      return;
    }

    windowSelection?.addRange(ReactEditor.toDOMRange(rteEditor, range));
    rteEditor.selection = range;
  }, [rteEditor, range]);

  const toggleToolbar = () => {
    if (isToolbarVisible) {
      closeToolbar();
    } else if (hasRange) {
      setToolbarVisible(true);
    }
  };

  const addLink = useCallback(
    (linkDefinition) => {
      closeToolbar();
      wrapLink(rteEditor, linkDefinition, { typeLink: BuiltInComponents.Link });
    },
    [closeToolbar, rteEditor],
  );

  const removeLink = useCallback(() => {
    closeToolbar();
    unwrapLink(rteEditor, { typeLink: BuiltInComponents.Link });
  }, [closeToolbar, rteEditor]);

  const onKeyDown = useMemo(
    () =>
      hotkeys({
        keyMap: {
          link: 'mod+k',
        },
        handlers: {
          link: () => {
            showToolbar();
          },
        },
      }),
    [],
  );

  let initialValue: LinkDefinition = { href: '', variables: [] };
  if (hasRange) {
    const definition = getDefinitionForLinkAtRange(rteEditor, range as Range);
    if (definition) {
      initialValue = definition;
    }
  }

  // either is in edit mode, or the selected range has a value set to it
  const isButtonActive = isToolbarVisible || (initialValue.href && hasRange);
  const isRangeNotEmpty = !!range && Range.isExpanded(range) && Editor.string(rteEditor, range);

  return (
    <EventListener
      target="window"
      onKeyDown={(e: any) => {
        if (hasRange) {
          e.stopPropagation();
          onKeyDown(e);
        }
      }}
    >
      {isToolbarVisible && (
        <Callout
          referenceElement={ref.current}
          onClose={() => setToolbarVisible(false)}
          closeKey="Escape"
          placement="top-start"
          usePortal={false}
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
          ]}
        >
          {(calloutProps) => (
            <div
              data-craft-ignore-events
              data-focus-listener={NO_ACTION}
              ref={calloutProps.ref}
              style={calloutProps.style}
              {...calloutProps.attributes.popper}
            >
              <LinkToolbar
                initialValue={initialValue}
                onClose={closeToolbar}
                onLink={addLink}
                onUnlink={removeLink}
              />
            </div>
          )}
        </Callout>
      )}
      <Tooltip
        placement="top"
        offset="xxs"
        content={<TooltipBodyWithShortcuts content="Embed Link" shortcuts={['mod', 'K']} />}
      >
        <ButtonIcon
          ref={ref}
          size="small"
          icon={LinkUnbrokenIcon}
          active={!!isButtonActive}
          onClick={() => window.requestAnimationFrame(toggleToolbar)}
          radius="xsmall"
          disabled={(!isRangeNotEmpty && !isButtonActive) || disabled}
          color="white"
        />
      </Tooltip>
    </EventListener>
  );
};
